/**
 * Authentication Service
 * 
 * Handles user authentication, token management, and profile retrieval.
 * This service is stateless and relies on browser storage for persistence.
 * 
 * @remarks
 * - Tokens are stored in localStorage (when "Keep me signed in" is checked) or sessionStorage otherwise
 * - Service methods are pure and don't maintain internal state
 * - All HTTP operations return Observables for reactive composition
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, finalize, map, shareReplay, tap, throwError } from 'rxjs';
import { TokenResponse } from '../api/models/token-response';
import { ApiConfiguration } from '../api/api-configuration';
import { UserResponse } from '../api/models/user-response';
import { StorageKeys } from '../core/constants';
import { SKIP_AUTH } from '../core/interceptors/auth.interceptor';

interface LoginCredentials {
  email: string;
  password: string;
  keepMeSignedIn?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  
  private readonly API_AUTH_BASE = `${this.apiConfig.rootUrl}/api/v1/auth`;
  
  /**
   * Authenticates user with credentials and stores tokens
   * @param email - User's email (login identifier)
   * @param password - User's password
   * @param rememberMe - If true, store tokens in localStorage; if false, in sessionStorage
   * @returns Observable of token response
   */
  login(email: string, password: string, rememberMe?: boolean): Observable<TokenResponse> {
    const credentials: LoginCredentials = {
      email,
      password,
      keepMeSignedIn: rememberMe
    };
    
    return this.http.post<TokenResponse>(
      `${this.API_AUTH_BASE}/login`,
      credentials
    ).pipe(
      tap((tokenData) => this.storeTokens(tokenData, rememberMe))
    );
  }

  /**
   * Requests a passwordless sign-in link (public endpoint; response is generic for privacy).
   */
  requestMagicLink(email: string): Observable<{ message: string }> {
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.post<{ message: string }>(
      `${this.API_AUTH_BASE}/magic-link`,
      { email },
      { context }
    );
  }

  /**
   * Exchanges a one-time magic-link token for access and refresh tokens.
   */
  verifyMagicLink(token: string, rememberMe?: boolean): Observable<TokenResponse> {
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.post<TokenResponse>(
      `${this.API_AUTH_BASE}/magic-link/verify`,
      { token, keepMeSignedIn: rememberMe },
      { context }
    ).pipe(
      tap((tokenData) => this.storeTokens(tokenData, rememberMe))
    );
  }

  /**
   * Exchanges a Google OAuth2 access token (from the popup flow) for access and refresh tokens.
   */
  loginWithGoogle(accessToken: string): Observable<TokenResponse> {
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.post<TokenResponse>(
      `${this.API_AUTH_BASE}/google`,
      { accessToken },
      { context }
    ).pipe(
      tap((tokenData) => this.storeTokens(tokenData))
    );
  }

  /**
   * Exchanges a LinkedIn OAuth2 authorization code (from the popup callback) for access and
   * refresh tokens. The code-for-token exchange happens server-side (requires the client
   * secret), so unlike Google this sends a code, not an access token.
   */
  loginWithLinkedIn(code: string, redirectUri: string): Observable<TokenResponse> {
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.post<TokenResponse>(
      `${this.API_AUTH_BASE}/linkedin`,
      { code, redirectUri },
      { context }
    ).pipe(
      tap((tokenData) => this.storeTokens(tokenData))
    );
  }

  /**
   * Updates the current user's org-wide consent to share anonymized data for industry
   * benchmarking (Profile > Preferences > "Stop sharing data" toggle).
   */
  updateDataSharingPreference(optIn: boolean): Observable<{ dataSharingOptIn: boolean }> {
    return this.http.patch<{ dataSharingOptIn: boolean }>(
      `${this.API_AUTH_BASE}/data-sharing-preference`,
      { optIn }
    );
  }

  /**
   * Completes account setup for a first-time Google sign-up by creating the organization
   * (customer profile) the traditional signup wizard would otherwise have created up front.
   */
  completeGoogleProfile(payload: {
    organizationName: string;
    industry?: string;
    country?: string;
    mobile: string;
    shareDataForBenchmark: boolean;
  }): Observable<UserResponse> {
    return this.http.post<UserResponse>(
      `${this.API_AUTH_BASE}/complete-profile`,
      payload
    ).pipe(
      tap((user) => this.currentUserSubject.next(user))
    );
  }

  /**
   * Records "how did you hear about us?" — asked as a follow-up right after a first-time
   * Google sign-up completes their profile.
   */
  updateReferralSource(payload: { referralSource: string; referredByEmail?: string }): Observable<UserResponse> {
    return this.http.patch<UserResponse>(
      `${this.API_AUTH_BASE}/referral-source`,
      payload
    ).pipe(
      tap((user) => this.currentUserSubject.next(user))
    );
  }

  /**
   * Requests a new email verification link (public endpoint; response is generic for privacy).
   */
  resendVerificationEmail(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.API_AUTH_BASE}/resend-verification-email`,
      { email }
    );
  }
  
  /**
   * Retrieves the current user's profile information
   * @returns Observable of user response
   */
  getProfile(refresh = false): Observable<UserResponse> {
    if (refresh || !this.profileRequest$) {
      this.profileRequest$ = this.http
        .get<UserResponse>(`${this.API_AUTH_BASE}/profile`)
        .pipe(
          tap((user) => this.currentUserSubject.next(user)),
          shareReplay(1)
        );
    }

    return this.profileRequest$;
  }
  
  /**
   * Logs out the current user and clears all stored tokens
   */
  logout(): void {
    this.clearAllTokens();
    this.profileRequest$ = undefined;
    this.currentUserSubject.next(null);
  }
  
  /**
   * Retrieves the stored access token
   * @returns Access token string or null if not found
   */
  getToken(): string | null {
    return localStorage.getItem(this.storageKey(StorageKeys.ACCESS_TOKEN)) ||
           sessionStorage.getItem(this.storageKey(StorageKeys.ACCESS_TOKEN));
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.storageKey(StorageKeys.REFRESH_TOKEN)) ||
           sessionStorage.getItem(this.storageKey(StorageKeys.REFRESH_TOKEN));
  }

  /**
   * Exchanges the stored refresh token for a new access token (single-flight).
   */
  refreshAccessToken(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    if (!this.refreshInFlight$) {
      const context = new HttpContext().set(SKIP_AUTH, true);
      this.refreshInFlight$ = this.http
        .post<TokenResponse>(
          `${this.API_AUTH_BASE}/refresh`,
          { refreshToken },
          { context }
        )
        .pipe(
          tap((tokenData) => this.storeTokens(tokenData, this.isRememberMeStorage())),
          map((tokenData) => {
            const accessToken = this.extractAccessToken(tokenData);
            if (!accessToken) {
              throw new Error('Refresh response missing access token');
            }
            return accessToken;
          }),
          shareReplay(1),
          finalize(() => {
            this.refreshInFlight$ = undefined;
          })
        );
    }

    return this.refreshInFlight$;
  }

  /**
   * Checks if user is currently authenticated
   * @returns True if user has a valid token
   */
  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  private refreshInFlight$?: Observable<string>;
  private profileRequest$?: Observable<UserResponse>;
  private readonly currentUserSubject = new BehaviorSubject<UserResponse | null>(null);

  /** Emits the authenticated user's profile after a successful `/profile` fetch. */
  readonly currentUser$ = this.currentUserSubject.asObservable();
  
  /**
   * Stores authentication tokens in localStorage or sessionStorage based on rememberMe
   * @param tokenData - Token response from login API
   * @param rememberMe - If true, use localStorage; if false, use sessionStorage; if undefined, use localStorage
   * @private
   */
  private isRememberMeStorage(): boolean {
    return !!(
      localStorage.getItem(this.storageKey(StorageKeys.ACCESS_TOKEN)) ||
      localStorage.getItem(this.storageKey(StorageKeys.REFRESH_TOKEN))
    );
  }

  private storeTokens(tokenData: TokenResponse, rememberMe?: boolean): void {
    const storage = rememberMe === false ? sessionStorage : localStorage;

    const accessToken = this.extractAccessToken(tokenData);
    const refreshToken = this.extractRefreshToken(tokenData);

    if (accessToken) {
      storage.setItem(this.storageKey(StorageKeys.ACCESS_TOKEN), accessToken);
      this.cleanupLegacyTokens();
    }

    if (refreshToken) {
      storage.setItem(this.storageKey(StorageKeys.REFRESH_TOKEN), refreshToken);
    }
  }
  
  /**
   * Extracts access token from various response formats
   * @private
   */
  private extractAccessToken(tokenData: TokenResponse): string | undefined {
    // Check for standard OAuth/JWT response formats
    if ('accessToken' in tokenData && typeof tokenData.accessToken === 'string') {
      return tokenData.accessToken;
    }
    if ('access_token' in tokenData && typeof tokenData.access_token === 'string') {
      return tokenData.access_token;
    }
    if ('token' in tokenData && typeof tokenData.token === 'string') {
      return tokenData.token;
    }
    return undefined;
  }
  
  /**
   * Extracts refresh token from various response formats
   * @private
   */
  private extractRefreshToken(tokenData: TokenResponse): string | undefined {
    if ('refreshToken' in tokenData && typeof tokenData.refreshToken === 'string') {
      return tokenData.refreshToken;
    }
    if ('refresh_token' in tokenData && typeof tokenData.refresh_token === 'string') {
      return tokenData.refresh_token;
    }
    return undefined;
  }
  
  /**
   * Clears all authentication tokens from storage
   * @private
   */
  private clearAllTokens(): void {
    // Clear current tokens
    localStorage.removeItem(this.storageKey(StorageKeys.ACCESS_TOKEN));
    localStorage.removeItem(this.storageKey(StorageKeys.REFRESH_TOKEN));
    sessionStorage.removeItem(this.storageKey(StorageKeys.ACCESS_TOKEN));
    sessionStorage.removeItem(this.storageKey(StorageKeys.REFRESH_TOKEN));
    
    // Clear legacy tokens
    this.cleanupLegacyTokens();
  }
  
  /**
   * Removes deprecated token keys
   * @private
   */
  private cleanupLegacyTokens(): void {
    localStorage.removeItem(StorageKeys.ACCESS_TOKEN);
    localStorage.removeItem(StorageKeys.REFRESH_TOKEN);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem(StorageKeys.ACCESS_TOKEN);
    sessionStorage.removeItem(StorageKeys.REFRESH_TOKEN);
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('refresh_token');
  }

  private storageKey(key: string): string {
    const apiRoot = (this.apiConfig.rootUrl || 'default').replace(/\/+$/, '');
    return `appdoctor:${apiRoot}:${key}`;
  }
}
